/*
 *  MojeAktaImigracyjne — placeholder grassroots landing for the
 *  "My Immigration Record" people-first surface.
 *
 *  Strict scope (Phase 2.3.x "Start My Immigration Record" CTA):
 *    - Empty-state only. No form. No upload. No backend call.
 *    - Carries the doctrine + safety + record-vs-status boundaries.
 *    - mailto: signup-interest link (no form, no data layer).
 *    - Reachable ONLY via the CaseHubPublic CTA; not in top nav yet.
 *
 *  This page must keep saying that HussarAi organizes the record and
 *  review history, NOT the legal status. Audience: refugees, families,
 *  workers, students, spouses, churches, schools, helpers.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Mail,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ClipboardList,
  Printer,
  Loader2,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';

const MojeAktaImigracyjne = () => {
  const { t, i18n } = useTranslation();

  // Draft-creation form state — strictly the minimal, privacy-safe fields.
  // No uploads, no passport / PESEL / national-ID inputs, no sensitive PII.
  const [draftForm, setDraftForm] = useState({
    email: '',
    preferred_language: (i18n.language || 'pl').toLowerCase().startsWith('pl') ? 'pl' : 'en',
    record_owner_type: 'self',
    short_context: '',
    country_context: '',
    current_location_country: '',
    target_country: '',
    helper_type: '',
    safety_acknowledged: false,
    consent_acknowledged: false,
  });
  const [draftSubmitting, setDraftSubmitting] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [draftSuccess, setDraftSuccess] = useState(null);

  const ownerOptions = [
    'self', 'family_member', 'helper', 'lawyer', 'ngo',
    'employer', 'school', 'other',
  ];
  const helperOptions = [
    'none', 'lawyer', 'ngo_worker', 'social_worker', 'church_volunteer',
    'school_staff', 'employer', 'family_member', 'friend', 'other',
  ];

  const handleDraftFieldChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setDraftForm((prev) => ({ ...prev, [field]: value }));
  };

  const resolveErrorMessage = (errorCode) => {
    const known = ['consent_required', 'safety_acknowledgement_required',
      'sensitive_field_not_allowed', 'invalid_request', 'rate_limited'];
    const key = known.includes(errorCode) ? errorCode : 'generic';
    return t(`mojeAkta.draftForm.errors.${key}`);
  };

  const handleDraftSubmit = async (e) => {
    e.preventDefault();
    setDraftError(null);
    setDraftSuccess(null);

    if (!draftForm.consent_acknowledged) {
      setDraftError(resolveErrorMessage('consent_required'));
      return;
    }
    if (!draftForm.safety_acknowledged) {
      setDraftError(resolveErrorMessage('safety_acknowledgement_required'));
      return;
    }

    setDraftSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const body = {
        email: draftForm.email.trim(),
        preferred_language: draftForm.preferred_language,
        record_owner_type: draftForm.record_owner_type,
        short_context: draftForm.short_context.trim(),
        consent_acknowledged: draftForm.consent_acknowledged,
        safety_acknowledged: draftForm.safety_acknowledged,
      };
      if (draftForm.country_context.trim()) {
        body.country_context = draftForm.country_context.trim();
      }
      if (draftForm.current_location_country.trim()) {
        body.current_location_country = draftForm.current_location_country.trim();
      }
      if (draftForm.target_country.trim()) {
        body.target_country = draftForm.target_country.trim();
      }
      if (draftForm.helper_type && draftForm.helper_type !== 'none') {
        body.helper_type = draftForm.helper_type;
      }

      const resp = await fetch(`${apiBase}/api/immigration-records/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        if (resp.status === 429) {
          setDraftError(resolveErrorMessage('rate_limited'));
        } else if (data && data.detail && data.detail.code) {
          setDraftError(resolveErrorMessage(data.detail.code));
        } else {
          setDraftError(resolveErrorMessage('generic'));
        }
        return;
      }

      setDraftSuccess({
        case_id: data.case_id,
        status: data.status,
        next_step: data.next_step,
        disclaimer: data.disclaimer,
      });
    } catch (_err) {
      setDraftError(resolveErrorMessage('generic'));
    } finally {
      setDraftSubmitting(false);
    }
  };

  useEffect(() => {
    document.title = t('mojeAkta.metaTitle');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('mojeAkta.metaDescription'));
  }, [t]);

  const subject = encodeURIComponent(t('mojeAkta.interestSubject'));
  const email = t('mojeAkta.interestEmail');

  const willDoItems = t('mojeAkta.willDo.items', { returnObjects: true }) || [];
  const willNotDoItems = t('mojeAkta.willNotDo.items', { returnObjects: true }) || [];
  const checklistGroups = t('mojeAkta.checklist.groups', { returnObjects: true }) || [];

  const handlePrintChecklist = () => {
    if (typeof window !== 'undefined' && typeof window.print === 'function') {
      window.print();
    }
  };

  // Shared input styling — kept inline so the people-first surface stays
  // self-contained and the form is readable on the dark page background.
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    background: 'rgba(15,23,42,0.85)',
    border: '1px solid rgba(125,211,252,0.30)',
    color: '#E2E8F0',
    fontSize: 14,
    lineHeight: 1.4,
    outline: 'none',
  };
  const helperStyle = {
    fontSize: 11, lineHeight: 1.45,
    color: 'rgba(226,232,240,0.62)',
    marginTop: 2,
  };

  return (
    <main
      data-testid="moje-akta-page"
      style={{
        minHeight: '100vh',
        background: 'var(--hussar-bg-page, #0B1220)',
        color: 'var(--hussar-text-primary, #E5E7EB)',
        padding: '64px 20px 96px',
      }}
    >
      <HussarTopNav />
      {/* Print-only stylesheet — hide background-heavy / interactive
          elements when the user prints the checklist. Keeps the heading,
          group cards, and the safety strip visible. No auto-print. */}
      <style data-testid="moje-akta-print-style">{`
        @media print {
          body {
            background: #ffffff !important;
            color: #000000 !important;
          }
          main[data-testid="moje-akta-page"] {
            background: #ffffff !important;
            color: #000000 !important;
            padding: 16px !important;
          }
          .hussar-print-hide,
          [data-testid="moje-akta-empty-state-card"],
          [data-testid="moje-akta-will-do-card"],
          [data-testid="moje-akta-will-not-do-card"],
          [data-testid="moje-akta-safety-card"],
          [data-testid="moje-akta-record-vs-status-card"],
          [data-testid="moje-akta-bridge-doctrine-card"],
          [data-testid="moje-akta-back-link"],
          nav, header, footer {
            display: none !important;
          }
          [data-testid="moje-akta-checklist-section"] {
            page-break-before: auto;
          }
          [data-testid^="moje-akta-checklist-group-"] {
            page-break-inside: avoid;
            break-inside: avoid;
            background: #ffffff !important;
            border: 1px solid #cccccc !important;
            color: #000000 !important;
          }
          [data-testid="moje-akta-checklist-heading"],
          [data-testid^="moje-akta-checklist-group-"][data-testid$="-title"],
          [data-testid="moje-akta-checklist-safety-title"] {
            color: #000000 !important;
          }
          [data-testid="moje-akta-checklist-safety-card"] {
            background: #ffffff !important;
            border: 1px solid #999999 !important;
            border-left: 3px solid #000000 !important;
            color: #000000 !important;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="hussar-container" style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* Eyebrow + H1 */}
        <p
          data-testid="moje-akta-eyebrow"
          style={{
            fontSize: 11, letterSpacing: 1.2, fontWeight: 700,
            color: '#38BDF8', margin: 0,
          }}
        >
          {t('mojeAkta.pageEyebrow')}
        </p>

        <h1
          data-testid="moje-akta-h1"
          style={{
            fontSize: 'clamp(28px, 5vw, 44px)',
            fontWeight: 800,
            lineHeight: 1.15,
            margin: '14px 0 10px',
            color: '#F1F5F9',
          }}
        >
          {t('mojeAkta.pageH1')}
        </h1>

        <p
          data-testid="moje-akta-subtitle"
          style={{
            fontSize: 16, lineHeight: 1.6,
            color: 'rgba(226,232,240,0.78)',
            marginTop: 4, marginBottom: 0,
          }}
        >
          {t('mojeAkta.pageSubtitle')}
        </p>

        {/* v4.0 Phase 5 — hero safety lines (three-line lock) */}
        <ul
          data-testid="moje-akta-hero-safety-lines"
          style={{
            listStyle: 'none', padding: 0, margin: '18px 0 0',
            display: 'grid', gap: 6,
            fontSize: 13, lineHeight: 1.55,
            color: 'rgba(226,232,240,0.72)',
          }}
        >
          <li data-testid="moje-akta-hero-safety-not-legal-advice">
            {t('mojeAkta.heroSafetyLines.notLegalAdvice')}
          </li>
          <li data-testid="moje-akta-hero-safety-no-guarantee">
            {t('mojeAkta.heroSafetyLines.noGuarantee')}
          </li>
          <li data-testid="moje-akta-hero-safety-human-review">
            {t('mojeAkta.heroSafetyLines.humanReview')}
          </li>
        </ul>

        {/* v4.0 Phase 5 — hero CTA anchoring the draft flow */}
        <a
          href="#moje-akta-draft-form"
          data-testid="moje-akta-hero-cta"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            marginTop: 22,
            padding: '12px 22px', borderRadius: 8,
            fontWeight: 700, background: '#7DD3FC',
            color: '#0B1220', textDecoration: 'none', fontSize: 15,
          }}
        >
          {t('mojeAkta.heroCtaLabel')}
        </a>

        {/* Coming-soon badge */}
        <div style={{ marginTop: 22 }}>
          <span
            data-testid="moje-akta-coming-soon-badge"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 999,
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.32)',
              color: '#F59E0B',
              fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
            }}
          >
            {t('mojeAkta.comingSoonLabel')}
          </span>
        </div>

        {/* Empty-state card */}
        <Card
          data-testid="moje-akta-empty-state-card"
          style={{
            marginTop: 22,
            padding: '22px 22px',
            background: 'rgba(15,23,42,0.55)',
            border: '1px solid rgba(56,189,248,0.20)',
            borderRadius: 12,
          }}
        >
          <h2
            data-testid="moje-akta-empty-state-title"
            style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#E2E8F0' }}
          >
            {t('mojeAkta.emptyStateTitle')}
          </h2>
          <p
            data-testid="moje-akta-empty-state-body"
            style={{
              fontSize: 14, lineHeight: 1.6, marginTop: 10,
              color: 'rgba(226,232,240,0.82)',
            }}
          >
            {t('mojeAkta.emptyStateBody')}
          </p>
          <p
            data-testid="moje-akta-audience-line"
            style={{
              fontSize: 12, marginTop: 14,
              color: '#38BDF8', letterSpacing: 0.3,
            }}
          >
            {t('mojeAkta.audienceLine')}
          </p>

          {/* Signup-interest via mailto only (no form, no backend). */}
          <p
            data-testid="moje-akta-interest-line"
            style={{
              fontSize: 13, marginTop: 18,
              color: 'rgba(226,232,240,0.82)',
            }}
          >
            {t('mojeAkta.interestLine')}
          </p>
          <a
            data-testid="moje-akta-interest-mailto"
            href={`mailto:${email}?subject=${subject}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              marginTop: 8,
              padding: '10px 18px', borderRadius: 8,
              background: 'rgba(56,189,248,0.10)',
              border: '1px solid rgba(56,189,248,0.36)',
              color: '#38BDF8',
              fontWeight: 600, textDecoration: 'none',
              fontSize: 14,
            }}
          >
            <Mail className="w-4 h-4" aria-hidden="true" />
            {t('mojeAkta.interestCtaLabel')}
          </a>
        </Card>

        {/* Draft-creation form — minimal, privacy-safe people-first surface.
            Creates a server-side ``immigration_cases`` draft via
            POST /api/immigration-records/drafts. No uploads, no document
            numbers, no PII beyond a hashed email + a short context. */}
        <Card
          id="moje-akta-draft-form"
          data-testid="moje-akta-draft-form-card"
          style={{
            marginTop: 18,
            padding: '22px 22px',
            background: 'rgba(15,23,42,0.55)',
            border: '1px solid rgba(56,189,248,0.30)',
            borderLeft: '3px solid #38BDF8',
            borderRadius: 12,
          }}
        >
          <h2
            data-testid="moje-akta-draft-form-heading"
            style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#E2E8F0' }}
          >
            {t('mojeAkta.draftForm.heading')}
          </h2>
          <p
            data-testid="moje-akta-draft-form-subheading"
            style={{
              fontSize: 13, lineHeight: 1.55, marginTop: 8,
              color: 'rgba(226,232,240,0.78)',
            }}
          >
            {t('mojeAkta.draftForm.subheading')}
          </p>

          {/* "What this form does NOT collect" strip — privacy boundary
              shown BEFORE the inputs so the user knows the limits up front. */}
          <Card
            data-testid="moje-akta-draft-no-uploads-card"
            style={{
              marginTop: 14,
              padding: '12px 14px',
              background: 'rgba(248,113,113,0.04)',
              border: '1px solid rgba(248,113,113,0.22)',
              borderLeft: '3px solid #F87171',
              borderRadius: 10,
            }}
          >
            <p
              data-testid="moje-akta-draft-no-uploads-title"
              style={{
                fontSize: 11, letterSpacing: 1.1, fontWeight: 700,
                color: '#F87171', margin: 0,
              }}
            >
              {t('mojeAkta.draftForm.noUploadsTitle')}
            </p>
            <ul
              data-testid="moje-akta-draft-no-uploads-list"
              style={{
                marginTop: 8, paddingLeft: 0, listStyle: 'none',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}
            >
              {(t('mojeAkta.draftForm.noUploadsItems', { returnObjects: true }) || [])
                .map((item, idx) => (
                  <li
                    key={item}
                    data-testid={`moje-akta-draft-no-uploads-item-${idx}`}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      fontSize: 12, lineHeight: 1.5,
                      color: 'rgba(226,232,240,0.80)',
                    }}
                  >
                    <XCircle className="w-3 h-3" aria-hidden="true"
                      style={{ color: '#F87171', marginTop: 4, flexShrink: 0 }} />
                    <span>{item}</span>
                  </li>
                ))}
            </ul>
          </Card>

          {draftSuccess ? (
            <div
              data-testid="moje-akta-draft-success-panel"
              style={{
                marginTop: 18,
                padding: '16px 16px',
                background: 'rgba(34,197,94,0.06)',
                border: '1px solid rgba(34,197,94,0.32)',
                borderLeft: '3px solid #22C55E',
                borderRadius: 10,
              }}
            >
              <p
                data-testid="moje-akta-draft-success-title"
                style={{
                  fontSize: 14, fontWeight: 700, margin: 0, color: '#86EFAC',
                }}
              >
                {t('mojeAkta.draftForm.successTitle')}
              </p>
              <dl
                style={{
                  marginTop: 12, display: 'grid',
                  gridTemplateColumns: '160px 1fr', gap: '6px 12px',
                  fontSize: 13, color: 'rgba(226,232,240,0.88)',
                }}
              >
                <dt style={{ opacity: 0.7 }}>
                  {t('mojeAkta.draftForm.successCaseRefLabel')}
                </dt>
                <dd
                  data-testid="moje-akta-draft-success-case-id"
                  style={{ margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {draftSuccess.case_id}
                </dd>
                <dt style={{ opacity: 0.7 }}>
                  {t('mojeAkta.draftForm.successStatusLabel')}
                </dt>
                <dd
                  data-testid="moje-akta-draft-success-status"
                  style={{ margin: 0 }}
                >
                  {draftSuccess.status}
                </dd>
                <dt style={{ opacity: 0.7 }}>
                  {t('mojeAkta.draftForm.successNextStepLabel')}
                </dt>
                <dd
                  data-testid="moje-akta-draft-success-next-step"
                  style={{ margin: 0 }}
                >
                  {draftSuccess.next_step}
                </dd>
                <dt style={{ opacity: 0.7 }}>
                  {t('mojeAkta.draftForm.successDisclaimerLabel')}
                </dt>
                <dd
                  data-testid="moje-akta-draft-success-disclaimer"
                  style={{ margin: 0, fontStyle: 'italic', opacity: 0.85 }}
                >
                  {draftSuccess.disclaimer}
                </dd>
              </dl>
            </div>
          ) : (
            <form
              data-testid="moje-akta-draft-form"
              onSubmit={handleDraftSubmit}
              style={{
                marginTop: 16,
                display: 'flex', flexDirection: 'column', gap: 14,
              }}
              noValidate
            >
              {/* Email */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label
                  htmlFor="moje-akta-draft-email"
                  style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                >
                  {t('mojeAkta.draftForm.emailLabel')}
                </label>
                <input
                  id="moje-akta-draft-email"
                  data-testid="moje-akta-draft-email-input"
                  type="email"
                  required
                  value={draftForm.email}
                  onChange={handleDraftFieldChange('email')}
                  autoComplete="email"
                  style={inputStyle}
                />
                <span style={helperStyle}>{t('mojeAkta.draftForm.emailHelp')}</span>
              </div>

              {/* Preferred language */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label
                  htmlFor="moje-akta-draft-preferred-language"
                  style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                >
                  {t('mojeAkta.draftForm.preferredLanguageLabel')}
                </label>
                <select
                  id="moje-akta-draft-preferred-language"
                  data-testid="moje-akta-draft-preferred-language-select"
                  value={draftForm.preferred_language}
                  onChange={handleDraftFieldChange('preferred_language')}
                  style={inputStyle}
                >
                  <option value="pl">{t('mojeAkta.draftForm.preferredLanguagePl')}</option>
                  <option value="en">{t('mojeAkta.draftForm.preferredLanguageEn')}</option>
                </select>
              </div>

              {/* Record owner type */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label
                  htmlFor="moje-akta-draft-record-owner"
                  style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                >
                  {t('mojeAkta.draftForm.recordOwnerLabel')}
                </label>
                <select
                  id="moje-akta-draft-record-owner"
                  data-testid="moje-akta-draft-record-owner-select"
                  value={draftForm.record_owner_type}
                  onChange={handleDraftFieldChange('record_owner_type')}
                  style={inputStyle}
                >
                  {ownerOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {t(`mojeAkta.draftForm.recordOwnerOptions.${opt}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Short context */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label
                  htmlFor="moje-akta-draft-short-context"
                  style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                >
                  {t('mojeAkta.draftForm.shortContextLabel')}
                </label>
                <textarea
                  id="moje-akta-draft-short-context"
                  data-testid="moje-akta-draft-short-context-input"
                  required
                  rows={4}
                  maxLength={600}
                  value={draftForm.short_context}
                  onChange={handleDraftFieldChange('short_context')}
                  placeholder={t('mojeAkta.draftForm.shortContextPlaceholder')}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <span style={helperStyle}>{t('mojeAkta.draftForm.shortContextHelp')}</span>
              </div>

              {/* Optional country context fields */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 12,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label
                    htmlFor="moje-akta-draft-country-context"
                    style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                  >
                    {t('mojeAkta.draftForm.countryContextLabel')}
                  </label>
                  <input
                    id="moje-akta-draft-country-context"
                    data-testid="moje-akta-draft-country-context-input"
                    type="text"
                    value={draftForm.country_context}
                    onChange={handleDraftFieldChange('country_context')}
                    placeholder={t('mojeAkta.draftForm.countryContextPlaceholder')}
                    style={inputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label
                    htmlFor="moje-akta-draft-current-location"
                    style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                  >
                    {t('mojeAkta.draftForm.currentLocationLabel')}
                  </label>
                  <input
                    id="moje-akta-draft-current-location"
                    data-testid="moje-akta-draft-current-location-input"
                    type="text"
                    value={draftForm.current_location_country}
                    onChange={handleDraftFieldChange('current_location_country')}
                    style={inputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label
                    htmlFor="moje-akta-draft-target-country"
                    style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                  >
                    {t('mojeAkta.draftForm.targetCountryLabel')}
                  </label>
                  <input
                    id="moje-akta-draft-target-country"
                    data-testid="moje-akta-draft-target-country-input"
                    type="text"
                    value={draftForm.target_country}
                    onChange={handleDraftFieldChange('target_country')}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Helper type (optional) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label
                  htmlFor="moje-akta-draft-helper-type"
                  style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}
                >
                  {t('mojeAkta.draftForm.helperTypeLabel')}
                </label>
                <select
                  id="moje-akta-draft-helper-type"
                  data-testid="moje-akta-draft-helper-type-select"
                  value={draftForm.helper_type || 'none'}
                  onChange={handleDraftFieldChange('helper_type')}
                  style={inputStyle}
                >
                  {helperOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {t(`mojeAkta.draftForm.helperTypeOptions.${opt}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Safety acknowledgement */}
              <label
                data-testid="moje-akta-draft-safety-ack-row"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  fontSize: 13, lineHeight: 1.5,
                  color: 'rgba(226,232,240,0.85)',
                  cursor: 'pointer',
                }}
              >
                <input
                  data-testid="moje-akta-draft-safety-ack-checkbox"
                  type="checkbox"
                  checked={draftForm.safety_acknowledged}
                  onChange={handleDraftFieldChange('safety_acknowledged')}
                  style={{ marginTop: 4, flexShrink: 0 }}
                />
                <span>{t('mojeAkta.draftForm.safetyAckLabel')}</span>
              </label>

              {/* Consent acknowledgement */}
              <label
                data-testid="moje-akta-draft-consent-ack-row"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  fontSize: 13, lineHeight: 1.5,
                  color: 'rgba(226,232,240,0.85)',
                  cursor: 'pointer',
                }}
              >
                <input
                  data-testid="moje-akta-draft-consent-ack-checkbox"
                  type="checkbox"
                  checked={draftForm.consent_acknowledged}
                  onChange={handleDraftFieldChange('consent_acknowledged')}
                  style={{ marginTop: 4, flexShrink: 0 }}
                />
                <span>{t('mojeAkta.draftForm.consentAckLabel')}</span>
              </label>

              {draftError && (
                <div
                  data-testid="moje-akta-draft-error-banner"
                  role="alert"
                  style={{
                    padding: '10px 12px',
                    background: 'rgba(248,113,113,0.08)',
                    border: '1px solid rgba(248,113,113,0.32)',
                    borderRadius: 8,
                    color: '#FCA5A5',
                    fontSize: 13, lineHeight: 1.5,
                  }}
                >
                  {draftError}
                </div>
              )}

              <button
                type="submit"
                data-testid="moje-akta-draft-submit-button"
                disabled={draftSubmitting}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '12px 20px', borderRadius: 8,
                  background: draftSubmitting ? 'rgba(56,189,248,0.16)' : 'rgba(56,189,248,0.20)',
                  border: '1px solid rgba(56,189,248,0.42)',
                  color: '#38BDF8',
                  fontWeight: 700, fontSize: 14,
                  cursor: draftSubmitting ? 'wait' : 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                {draftSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    {t('mojeAkta.draftForm.submittingLabel')}
                  </>
                ) : (
                  t('mojeAkta.draftForm.submitButtonLabel')
                )}
              </button>
            </form>
          )}
        </Card>

        {/* What the record builder WILL help you do — narrowed
            "Immigration Record Assistant" scope (strategic v2). */}
        <Card
          data-testid="moje-akta-will-do-card"
          style={{
            marginTop: 16,
            padding: '20px 22px',
            background: 'rgba(34,197,94,0.04)',
            border: '1px solid rgba(34,197,94,0.22)',
            borderLeft: '3px solid #22C55E',
            borderRadius: 12,
          }}
        >
          <h3
            data-testid="moje-akta-will-do-title"
            style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#86EFAC' }}
          >
            {t('mojeAkta.willDo.title')}
          </h3>
          <ul
            data-testid="moje-akta-will-do-list"
            style={{
              marginTop: 12, paddingLeft: 0, listStyle: 'none',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}
          >
            {willDoItems.map((item, idx) => (
              <li
                key={item}
                data-testid={`moje-akta-will-do-item-${idx}`}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  fontSize: 14, lineHeight: 1.55,
                  color: 'rgba(226,232,240,0.88)',
                }}
              >
                <CheckCircle2
                  className="w-4 h-4"
                  aria-hidden="true"
                  style={{ color: '#22C55E', marginTop: 3, flexShrink: 0 }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* What HussarAi WILL NOT do — strict scope boundary. */}
        <Card
          data-testid="moje-akta-will-not-do-card"
          style={{
            marginTop: 12,
            padding: '20px 22px',
            background: 'rgba(248,113,113,0.04)',
            border: '1px solid rgba(248,113,113,0.22)',
            borderLeft: '3px solid #F87171',
            borderRadius: 12,
          }}
        >
          <h3
            data-testid="moje-akta-will-not-do-title"
            style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#F87171' }}
          >
            {t('mojeAkta.willNotDo.title')}
          </h3>
          <ul
            data-testid="moje-akta-will-not-do-list"
            style={{
              marginTop: 12, paddingLeft: 0, listStyle: 'none',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}
          >
            {willNotDoItems.map((item, idx) => (
              <li
                key={item}
                data-testid={`moje-akta-will-not-do-item-${idx}`}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  fontSize: 13, lineHeight: 1.55,
                  color: 'rgba(226,232,240,0.82)',
                }}
              >
                <XCircle
                  className="w-4 h-4"
                  aria-hidden="true"
                  style={{ color: '#F87171', marginTop: 3, flexShrink: 0 }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Immigration Record Checklist — static, no inputs, no storage.
            Helps people understand what belongs in their record before the
            real builder exists. */}
        <section
          data-testid="moje-akta-checklist-section"
          aria-label={t('mojeAkta.checklist.heading')}
          style={{ marginTop: 28 }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
            }}
          >
            <ClipboardList
              className="w-5 h-5"
              aria-hidden="true"
              style={{ color: '#7DD3FC' }}
            />
            <h2
              data-testid="moje-akta-checklist-heading"
              style={{
                fontSize: 22, fontWeight: 700, margin: 0,
                color: '#F1F5F9',
              }}
            >
              {t('mojeAkta.checklist.heading')}
            </h2>
          </div>
          <p
            data-testid="moje-akta-checklist-subheading"
            style={{
              fontSize: 13, lineHeight: 1.55, margin: '4px 0 16px',
              color: 'rgba(226,232,240,0.72)',
            }}
          >
            {t('mojeAkta.checklist.subheading')}
          </p>

          {/* Print button row — calls window.print() only on click.
              No auto-print, no analytics, no tracking. */}
          <div
            className="hussar-print-hide"
            data-testid="moje-akta-print-row"
            style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center',
              gap: 14, margin: '0 0 18px',
            }}
          >
            <button
              type="button"
              data-testid="moje-akta-print-checklist-btn"
              onClick={handlePrintChecklist}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 8,
                background: 'rgba(125,211,252,0.10)',
                border: '1px solid rgba(125,211,252,0.36)',
                color: '#7DD3FC',
                fontWeight: 600, fontSize: 14,
                cursor: 'pointer',
              }}
            >
              <Printer className="w-4 h-4" aria-hidden="true" />
              {t('mojeAkta.checklist.printButtonLabel')}
            </button>
            <p
              data-testid="moje-akta-print-helper-text"
              style={{
                margin: 0, fontSize: 12, lineHeight: 1.5,
                color: 'rgba(226,232,240,0.65)', flex: '1 1 280px',
              }}
            >
              {t('mojeAkta.checklist.printHelperText')}
            </p>
          </div>

          <div
            data-testid="moje-akta-checklist-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 14,
            }}
          >
            {checklistGroups.map((group, gIdx) => (
              <Card
                key={group.title}
                data-testid={`moje-akta-checklist-group-${gIdx}`}
                style={{
                  padding: '18px 18px',
                  background: 'rgba(15,23,42,0.55)',
                  border: '1px solid rgba(125,211,252,0.18)',
                  borderRadius: 12,
                }}
              >
                <h3
                  data-testid={`moje-akta-checklist-group-${gIdx}-title`}
                  style={{
                    fontSize: 14, fontWeight: 700, margin: 0,
                    color: '#7DD3FC', letterSpacing: 0.2,
                  }}
                >
                  {group.title}
                </h3>
                <ul
                  data-testid={`moje-akta-checklist-group-${gIdx}-list`}
                  style={{
                    marginTop: 10, paddingLeft: 0, listStyle: 'none',
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}
                >
                  {(group.items || []).map((item, iIdx) => (
                    <li
                      key={item}
                      data-testid={`moje-akta-checklist-group-${gIdx}-item-${iIdx}`}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        fontSize: 13, lineHeight: 1.5,
                        color: 'rgba(226,232,240,0.85)',
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          marginTop: 7,
                          width: 5, height: 5, borderRadius: 999,
                          background: '#7DD3FC', flexShrink: 0,
                        }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          {/* Inline safety strip — checklist does NOT determine status. */}
          <Card
            data-testid="moje-akta-checklist-safety-card"
            style={{
              marginTop: 16,
              padding: '14px 16px',
              background: 'rgba(245,158,11,0.05)',
              border: '1px solid rgba(245,158,11,0.30)',
              borderLeft: '3px solid #F59E0B',
              borderRadius: 12,
            }}
          >
            <p
              data-testid="moje-akta-checklist-safety-title"
              style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
                color: '#F59E0B', margin: 0,
              }}
            >
              {t('mojeAkta.checklist.safetyTitle')}
            </p>
            <p
              data-testid="moje-akta-checklist-safety-body"
              style={{
                fontSize: 13, lineHeight: 1.55, marginTop: 6,
                color: 'rgba(226,232,240,0.85)',
              }}
            >
              {t('mojeAkta.checklist.safetyBody')}
            </p>
          </Card>
        </section>

        {/* Safety strip — explicit "what HussarAi does not do" reused
            from the homepage namespace. */}
        <Card
          data-testid="moje-akta-safety-card"
          style={{
            marginTop: 16,
            padding: '16px 18px',
            background: 'rgba(248,113,113,0.06)',
            border: '1px solid rgba(248,113,113,0.32)',
            borderRadius: 12,
          }}
        >
          <h3
            data-testid="moje-akta-safety-title"
            style={{ fontSize: 14, fontWeight: 700, margin: 0, color: '#F87171' }}
          >
            {t('brandPivot.safety.title')}
          </h3>
          <p
            data-testid="moje-akta-safety-body"
            style={{
              fontSize: 13, lineHeight: 1.55, marginTop: 8,
              color: 'rgba(226,232,240,0.82)',
            }}
          >
            {t('brandPivot.safety.body')}
          </p>
        </Card>

        {/* Subtle helper card pointing to the /wiedza knowledge index —
            placed in the safety / education area so it reads as guidance,
            not as a competing CTA against the checklist actions above. */}
        <Card
          data-testid="moje-akta-wiedza-link-card"
          style={{
            marginTop: 12,
            padding: '14px 18px',
            background: 'rgba(125,211,252,0.04)',
            border: '1px solid rgba(125,211,252,0.20)',
            borderRadius: 12,
          }}
        >
          <p
            data-testid="moje-akta-wiedza-link-helper"
            style={{
              fontSize: 13, lineHeight: 1.55, margin: 0,
              color: 'rgba(226,232,240,0.82)',
            }}
          >
            {t('wiedzaLink.mojeAktaHelper')}
          </p>
          <Link
            to="/wiedza"
            data-testid="moje-akta-wiedza-link"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              marginTop: 6, fontSize: 13, fontWeight: 600,
              color: '#7DD3FC', textDecoration: 'none',
            }}
          >
            {t('wiedzaLink.mojeAktaLabel')} →
          </Link>
        </Card>

        {/* Record-vs-status clarifier reused from the homepage namespace. */}
        <Card
          data-testid="moje-akta-record-vs-status-card"
          style={{
            marginTop: 12,
            padding: '16px 18px',
            background: 'rgba(56,189,248,0.06)',
            border: '1px solid rgba(56,189,248,0.32)',
            borderRadius: 12,
          }}
        >
          <h3
            data-testid="moje-akta-record-vs-status-title"
            style={{ fontSize: 14, fontWeight: 700, margin: 0, color: '#38BDF8' }}
          >
            <ShieldCheck
              className="w-4 h-4 inline mr-2"
              aria-hidden="true"
              style={{ verticalAlign: 'text-bottom' }}
            />
            {t('brandPivot.recordVsStatus.title')}
          </h3>
          <p
            data-testid="moje-akta-record-vs-status-body"
            style={{
              fontSize: 13, lineHeight: 1.55, marginTop: 8,
              color: 'rgba(226,232,240,0.82)',
            }}
          >
            {t('brandPivot.recordVsStatus.body')}
          </p>
        </Card>

        {/* Bridge-doctrine quote strip — the durable People · Helpers ·
            Institutions framing the new positioning anchors. */}
        <Card
          data-testid="moje-akta-bridge-doctrine-card"
          style={{
            marginTop: 12,
            padding: '16px 18px',
            background: 'rgba(125,211,252,0.04)',
            border: '1px solid rgba(125,211,252,0.22)',
            borderLeft: '3px solid #7DD3FC',
            borderRadius: 12,
          }}
        >
          <p
            data-testid="moje-akta-bridge-doctrine-label"
            style={{
              fontSize: 11, letterSpacing: 1.2, fontWeight: 700,
              color: '#7DD3FC', margin: 0,
            }}
          >
            {t('brandPivot.bridgeDoctrine.label')}
          </p>
          <p
            data-testid="moje-akta-bridge-doctrine-quote"
            style={{
              fontSize: 14, lineHeight: 1.6, marginTop: 6,
              color: 'rgba(226,232,240,0.92)',
              fontStyle: 'italic',
            }}
          >
            {t('brandPivot.bridgeDoctrine.quote')}
          </p>
        </Card>

        {/* Doctrine — last line, italic, lower opacity. */}
        <p
          data-testid="moje-akta-doctrine"
          style={{
            marginTop: 26,
            fontSize: 12, opacity: 0.6,
            fontStyle: 'italic',
            color: 'rgba(226,232,240,0.78)',
          }}
        >
          {t('brandPivot.trustLine')}
        </p>

        {/* Back link — keep navigation simple, no top-nav exposure yet. */}
        <div style={{ marginTop: 18 }}>
          <Link
            to="/imigracja-i-urzedy"
            data-testid="moje-akta-back-link"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: '#94A3B8',
              fontSize: 13,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            {t('mojeAkta.backToHubLabel')}
          </Link>
        </div>
      </div>
    </main>
  );
};

export default MojeAktaImigracyjne;
